"use client";

import { router } from "@inertiajs/react";
import { ChevronDown, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOCUMENT_STATUS_OPTIONS } from "@/constants/document-status";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useSectorFilter } from "@/hooks/use-sector-filter";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import type { DeliveryNote, PagyMetadata, RansackFilters } from "@/types";

import { columns } from "./columns";

interface DeliveryNotesDataTableProps {
  deliveryNotes: DeliveryNote[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  organizationSlug: string;
}

export function DeliveryNotesDataTable({
  deliveryNotes,
  pagy,
  filters,
  organizationSlug: _organizationSlug,
}: DeliveryNotesDataTableProps) {
  const routes = useOrganizationRoutes();

  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({
    filters,
    pagy,
    path: routes.deliveryNotes.index(),
  });

  const [statusValue, setStatusValue] = useState(filters.status_eq ?? "all");
  const [searchMode, setSearchMode] = useState<"id" | "number">("id");
  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue<string>(
    String(filters.sequential_id_eq ?? filters.number_cont ?? ""),
  );
  const [supplierOption, setSupplierOption] = useState<AsyncOption | null>(
    null,
  );

  const {
    sectorOption,
    loadSectorOptions,
    handleSectorSelect,
    resetSector,
    hasSectorFilter,
  } = useSectorFilter({
    filters,
    handleFilterChange,
    filterKey: "purchase_request_sector_id_eq",
    searchEndpoint: routes.sectors.search(),
  });

  useEffect(() => {
    setStatusValue(filters.status_eq ?? "all");
    if (filters.sequential_id_eq) {
      setSearchMode("id");
    } else if (filters.number_cont) {
      setSearchMode("number");
    } else {
      setSearchMode("id");
    }
  }, [filters.status_eq, filters.sequential_id_eq, filters.number_cont]);

  useEffect(() => {
    const supplierId = filters.supplier_id_eq;
    if (!supplierId) {
      if (supplierOption) {
        setSupplierOption(null);
      }
      return;
    }

    if (supplierOption && String(supplierOption.value) === String(supplierId)) {
      return;
    }

    let isActive = true;

    const fetchSelectedSupplier = async () => {
      try {
        const params = new URLSearchParams();
        params.append("ids[]", String(supplierId));
        const response = await fetch(
          `${routes.suppliers.search()}?${params.toString()}`,
        );
        if (!response.ok) return;
        const data = (await response.json()) as AsyncOption[];
        const match = data.find(
          (option) => String(option.value) === String(supplierId),
        );
        if (isActive) {
          setSupplierOption(
            match ? { value: String(match.value), label: match.label } : null,
          );
        }
      } catch {
        if (isActive) {
          setSupplierOption(null);
        }
      }
    };

    void fetchSelectedSupplier();

    return () => {
      isActive = false;
    };
  }, [filters.supplier_id_eq, routes, supplierOption]);

  useEffect(() => {
    const normalized = debouncedSearchValue.trim();
    const nextValue = normalized ? normalized : undefined;

    if (searchMode === "id") {
      if ((filters.sequential_id_eq ?? undefined) !== nextValue) {
        handleFilterChange({
          sequential_id_eq: nextValue,
          number_cont: undefined,
        });
      }
    } else {
      if ((filters.number_cont ?? undefined) !== nextValue) {
        handleFilterChange({
          number_cont: nextValue,
          sequential_id_eq: undefined,
        });
      }
    }
    // Only depend on debounced value + searchMode to avoid loops on server-driven filter changes.
  }, [debouncedSearchValue, searchMode]);

  const loadSupplierOptions = async (
    inputValue: string,
  ): Promise<AsyncOption[]> => {
    const params = new URLSearchParams();
    if (inputValue) {
      params.set("term", inputValue);
    }
    const currentId =
      filters.supplier_id_eq ??
      (supplierOption ? String(supplierOption.value) : undefined);
    if (currentId) {
      params.append("ids[]", String(currentId));
    }

    try {
      const response = await fetch(
        `${routes.suppliers.search()}?${params.toString()}`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );
      if (!response.ok) {
        return [];
      }
      const data = (await response.json()) as AsyncOption[];
      return data.map((option) => ({
        value: String(option.value),
        label: option.label,
      }));
    } catch {
      return [];
    }
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    handleFilterChange({
      status_eq: value === "all" ? undefined : value,
    });
  };

  const handleSupplierSelect = (option: AsyncOption | null) => {
    setSupplierOption(
      option ? { value: String(option.value), label: option.label } : null,
    );
    handleFilterChange({
      supplier_id_eq: option ? String(option.value) : undefined,
    });
  };

  const handleReset = () => {
    setStatusValue("all");
    setSearchValue("");
    setSearchMode("id");
    setSupplierOption(null);
    resetSector();

    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  const hasStatusFilter = statusValue !== "all";
  const hasSearchFilter = Boolean(searchValue.trim());
  const hasSupplierFilter =
    supplierOption !== null || Boolean(filters.supplier_id_eq);
  const hasActiveFilters =
    hasStatusFilter || hasSearchFilter || hasSupplierFilter || hasSectorFilter;

  const renderFilters = () => (
    <div className="flex flex-wrap items-center gap-3">
      <ButtonGroup>
        <InputGroup className="w-[280px]">
          <InputGroupInput
            placeholder={
              searchMode === "id" ? "Buscar por ID..." : "Buscar por número..."
            }
            type={searchMode === "id" ? "number" : "text"}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <InputGroupAddon align="inline-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <InputGroupButton variant="ghost" className="!pr-1.5 text-xs">
                  {searchMode === "id" ? "ID" : "Número"}{" "}
                  <ChevronDown className="size-3" />
                </InputGroupButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSearchMode("id")}>
                  ID
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSearchMode("number")}>
                  Número de Remito
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </InputGroupAddon>
        </InputGroup>
        <Select value={statusValue} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px] rounded-none border-l-0">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {DOCUMENT_STATUS_OPTIONS.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <AsyncCombobox
          value={supplierOption}
          onChange={handleSupplierSelect}
          loadOptions={loadSupplierOptions}
          defaultOptions={true}
          cacheOptions={true}
          placeholder="Filtrar por proveedor"
          buttonClassName="w-[200px] rounded-none border-l-0"
        />
        <AsyncCombobox
          value={sectorOption}
          onChange={handleSectorSelect}
          loadOptions={loadSectorOptions}
          defaultOptions={true}
          cacheOptions={true}
          placeholder="Filtrar por obra"
          buttonClassName="w-[180px] rounded-l-none border-l-0"
        />
      </ButtonGroup>
      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={handleReset}
          className="h-9 px-2 lg:px-3"
        >
          <Trash2 className="h-4 w-4" /> Limpiar filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {renderFilters()}

      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={deliveryNotes}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false}
          onRowClick={(deliveryNote) =>
            router.visit(routes.deliveryNotes.show(deliveryNote.id))
          }
        />
      </div>

      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="remitos"
      />
    </div>
  );
}
